import { CloseCircleOutlined } from "@ant-design/icons";
import { Button, Card, Checkbox, ConfigProvider, Flex, Switch, Typography } from "antd";
import CardSectionTitle from "components/CardSectionTitle.component";
import { ReadOnlyWhenRestricted } from "components/capability";
import { useAdminCapabilities } from "hooks/useAdminCapabilities";
import { AdminCapability } from "shared/utils/capabilities";
import { useWorkingHoursPreview } from "components/modals/WorkingHoursConflictWarningModal";
import dayjs from "dayjs";
import { weekdayName } from "shared/utils/dayjs";
import { useEffect, useState } from "react";
import { useSetWorkingHours } from "hooks/mutations/staff";
import { AutoCloseTimePicker } from "pages/schedule/drawer/AutoCloseTimePicker";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { ArchivedWorkingHourBlock, WorkingHour } from "shared/types";

const TIME_FORMAT = "HH:mm";
const TIME_MINUTE_STEP = 5;

// End must be strictly after start — the two pickers are independent (unlike
// a RangePicker, which enforces this structurally). The backend also rejects
// an inverted block (workingHourEntryParser's start_time < end_time refine,
// setWorkingHours in staff.ts); this is purely a same-page UX guard so the
// picker itself never lets one get built in the first place.
const disabledEndTime = (startTime: string) => {
  const [startHour, startMinute] = startTime.split(":").map(Number);
  return () => ({
    disabledHours: () => Array.from({ length: startHour }, (_, h) => h),
    disabledMinutes: (hour: number) =>
      hour === startHour
        ? Array.from({ length: startMinute + 1 }, (_, m) => m)
        : [],
  });
};

// A new block defaults to starting right where the day's last block (by end
// time) ends, so consecutive "add" clicks chain naturally instead of always
// landing on a fixed 18:00-20:00 that can trivially collide with an
// already-added evening block (the backend rejects overlapping same-day
// blocks, setWorkingHours in staff.ts; this just keeps the common case from
// hitting that rejection by default). It's a starting point the admin can
// still adjust, not a guarantee against every possible arrangement).
const nextBlockDefault = (existing: Block[]): Block => {
  if (existing.length === 0) return { start_time: "09:00", end_time: "11:00" };
  const lastEndTime = [...existing].sort((a, b) =>
    a.end_time.localeCompare(b.end_time),
  ).pop()!.end_time;
  const [h, m] = lastEndTime.split(":").map(Number);
  const startMinutes = h * 60 + m;
  const endMinutes = Math.min(startMinutes + 120, 23 * 60 + 59);
  const toHHmm = (mins: number) =>
    `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
  return { start_time: toHHmm(startMinutes), end_time: toHHmm(endMinutes) };
};

// Rows are keyed by the stored day number, Sunday = 0 .. Saturday = 6, which
// matches the server's working_hour.day and dayjs's .day(), so weekdayName()
// names them in the active language.

// Display order only (Monday..Sunday), matching the calendar's own week start
// — this app's dayjs instance overrides weekStart to 1 specifically so both
// languages get Monday weeks (see shared/utils/dayjs.ts). The stored day
// values themselves stay Sunday=0..Saturday=6, untouched.
const DISPLAY_DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

interface Block {
  start_time: string;
  end_time: string;
}

// Sentinel for "runs through the literal end of this calendar day" — paired
// with "00:00" this is the closest thing to a genuine all-day block the
// HH:mm model supports (see the HHMM_REGEX/"24:00" exception in
// spott-server's workingHourEntryParser). Never handed to a TimePicker —
// dayjs("24:00", "HH:mm") is invalid — the checkbox below replaces the
// picker pair entirely instead.
const ALL_DAY_BLOCK: Block = { start_time: "00:00", end_time: "24:00" };
const isAllDay = (blocks: Block[]): boolean =>
  blocks.length === 1 &&
  blocks[0].start_time === ALL_DAY_BLOCK.start_time &&
  blocks[0].end_time === ALL_DAY_BLOCK.end_time;

interface Row {
  day: number;
  // A day can carry more than one independent block (e.g. a 9-11am class and
  // a separate 6-9pm class) — an empty array means the day isn't worked.
  blocks: Block[];
}

// When the instructor has never been configured, suggest every day as
// Sepanjang hari (all day), rather than every day reading as off (visually
// identical to an instructor deliberately configured with zero working
// days). All 7 days, not just Monday-Friday: this is meant to be a truly
// zero-restriction starting point — the same "unrestricted until you
// actually narrow it" default assigned_all_class_types now uses for Kelas
// yang Ditugaskan — so accepting the suggestion as-is and saving it can
// never immediately conflict with an existing schedule, on any day of the
// week (a weekdays-only default would silently exclude weekend classes,
// which are common). This is a FORM-ONLY suggestion — nothing is saved
// until Simpan Perubahan is clicked, so a wrong guess never reads as
// confirmed data, and the working-hours conflict check (3D) still gates
// the actual save regardless.
// Shared by buildInitialRows below (from live `hours`) and the "Pulihkan
// pengaturan sebelumnya" handler (from an archived snapshot) — both are
// just a flat list of {day, start_time, end_time} blocks that need grouping
// into the 7-row weekly template.
const buildRowsFromBlocks = (
  blocks: { day: number; start_time: string; end_time: string }[],
): Row[] => {
  const byDay = new Map<number, Block[]>();
  blocks.forEach((h) => {
    byDay.set(h.day, [
      ...(byDay.get(h.day) ?? []),
      { start_time: h.start_time, end_time: h.end_time },
    ]);
  });
  return DISPLAY_DAY_ORDER.map((day) => ({ day, blocks: byDay.get(day) ?? [] }));
};

const buildInitialRows = (
  hours: WorkingHour[],
  workingHoursConfigured: boolean,
): Row[] => {
  if (!workingHoursConfigured) {
    return DISPLAY_DAY_ORDER.map((day) => ({ day, blocks: [ALL_DAY_BLOCK] }));
  }
  return buildRowsFromBlocks(hours);
};

interface InstructorWorkingHoursProps {
  staffId: string;
  hours: WorkingHour[];
  workingHoursConfigured: boolean;
  // Non-null when a prior real configuration was archived (Jam Kerja was
  // turned off then back on business-wide) and hasn't been restored or
  // superseded by a newer save since — see updateBusinessPolicies.
  archivedWorkingHours: ArchivedWorkingHourBlock[] | null;
}

// A fixed 7-row weekly template rather than an AntD Form: the row set never
// changes shape, so plain local state is simpler than form-managing a list.
const InstructorWorkingHours = ({
  staffId,
  hours,
  workingHoursConfigured,
  archivedWorkingHours,
}: InstructorWorkingHoursProps) => {
  const [rows, setRows] = useState<Row[]>(() =>
    buildInitialRows(hours, workingHoursConfigured),
  );
  const [dirty, setDirty] = useState(false);
  // Pembatasan Admin: the Manager's operational-settings toggle names
  // instructor availability, so a restricted Admin sees this page read-only.
  // The explicit `disabled={!dirty}` on the save button beats the disabled
  // context, so the restriction is OR'd in there by hand.
  const { isRestricted } = useAdminCapabilities();
  const restricted = isRestricted(AdminCapability.OperationalSettings);
  const { mutate: saveHours, mutateAsync: saveHoursAsync, isPending } = useSetWorkingHours();
  const { checkAndConfirm, workingHoursPreviewWarning } = useWorkingHoursPreview();
  const [isCheckingConflicts, setIsCheckingConflicts] = useState(false);
  // Locally dismisses the "Pulihkan Pengaturan Sebelumnya" button the moment
  // it's clicked — the archive is already loaded into the form at that
  // point, so leaving the button up is redundant/confusing (there's nothing
  // further to restore). The archive itself isn't touched server-side until
  // an actual save (setWorkingHours clears archived_working_hours then), so
  // this is purely a local "I've already used this" dismissal, reset below
  // whenever the admin navigates to a different instructor.
  const [restoredFromArchive, setRestoredFromArchive] = useState(false);

  // Re-hydrate only when navigating between instructors (staffId changes).
  // Deliberately NOT keyed on `hours` too: a post-save refetch lands the
  // exact same rows the user just submitted, so re-running this would be a
  // no-op at best; keeping the dep array to staffId-only just makes that
  // explicit instead of relying on the values happening to match.
  useEffect(() => {
    setRows(buildInitialRows(hours, workingHoursConfigured));
    setDirty(false);
    setRestoredFromArchive(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffId]);

  const updateBlocks = (day: number, blocks: Block[]) => {
    setRows((prev) => prev.map((r) => (r.day === day ? { ...r, blocks } : r)));
    setDirty(true);
  };

  // Loads the archived snapshot into the form — nothing is saved yet. The
  // admin reviews/adjusts as needed and saves through the normal Simpan
  // Perubahan flow, which already runs the working-hours conflict check
  // (checkAndConfirm below) before writing anything, so a schedule created
  // while this instructor was on the default gets caught there rather than
  // needing its own separate check here.
  const restoreArchived = () => {
    if (!archivedWorkingHours) return;
    setRows(buildRowsFromBlocks(archivedWorkingHours));
    setDirty(true);
    setRestoredFromArchive(true);
  };

  const onSave = async () => {
    const form = {
      staff_id: staffId,
      hours: rows.flatMap((r) => r.blocks.map((b) => ({ day: r.day, ...b }))),
    };
    setIsCheckingConflicts(true);
    try {
      await checkAndConfirm(
        form,
        () => {
          saveHours(form, { onSuccess: () => setDirty(false) });
        },
        // "Hapus Kelas & Simpan": awaited so the warning modal closes only
        // once the server has deleted the classes and saved the hours.
        async () => {
          await saveHoursAsync({ ...form, remove_affected_schedules: true });
          setDirty(false);
        },
      );
    } finally {
      setIsCheckingConflicts(false);
    }
  };

  return (
    <>
      <ReadOnlyWhenRestricted capability={AdminCapability.OperationalSettings}>
      <Card>
      <Flex vertical gap={18}>
        <Flex justify="space-between" align="flex-start" gap={16}>
          <CardSectionTitle
            title={translate("tabs.working_hours")}
            subtitle={translate("class_settings.employee_management.working_hours_desc")}
          />
          <Flex gap={8}>
            {archivedWorkingHours && !restoredFromArchive && (
              <Button onClick={restoreArchived}>
                {translate("class_settings.employee_management.restore_previous")}
              </Button>
            )}
            <Button
              type="primary"
              loading={isPending || isCheckingConflicts}
              disabled={!dirty || restricted}
              onClick={onSave}
            >
              {translate("button.save_changes")}
            </Button>
          </Flex>
        </Flex>

        <Flex vertical gap={4}>
          {rows.map((row) => (
            <Flex key={row.day} align="flex-start" gap={16} style={{ paddingBlock: 8 }}>
              <Typography.Text style={{ width: 90, flexShrink: 0, paddingTop: 5 }}>
                {weekdayName(row.day)}
              </Typography.Text>
              <Switch
                checked={row.blocks.length > 0}
                onChange={(checked) =>
                  updateBlocks(
                    row.day,
                    checked ? [{ start_time: "09:00", end_time: "17:00" }] : [],
                  )
                }
                style={{ marginTop: 3 }}
              />
              {row.blocks.length === 0 ? (
                <Typography.Text type="secondary" style={{ paddingTop: 5 }}>
                  {translate("class_settings.employee_management.not_working")}
                </Typography.Text>
              ) : (
                <Flex vertical gap={8}>
                  {/* Default checkbox border is too faint for an opt-in the
                      admin must notice — same darker/thicker border used for
                      RecurrenceSelect.tsx's "Waktu berbeda per hari" checkbox. */}
                  <ConfigProvider theme={{ token: { colorBorder: colors.ink3, lineWidth: 1.5 } }}>
                    <Checkbox
                      checked={isAllDay(row.blocks)}
                      onChange={(e) =>
                        updateBlocks(
                          row.day,
                          e.target.checked
                            ? [ALL_DAY_BLOCK]
                            : [{ start_time: "09:00", end_time: "17:00" }],
                        )
                      }
                      style={{ marginTop: 3 }}
                    >
                      {translate("class_settings.employee_management.all_day")}
                    </Checkbox>
                  </ConfigProvider>
                  {!isAllDay(row.blocks) && (
                    <>
                      {row.blocks.map((block, index) => (
                        <Flex key={index} align="center" gap={4}>
                          <AutoCloseTimePicker
                            format={TIME_FORMAT}
                            minuteStep={TIME_MINUTE_STEP}
                            allowClear={false}
                            value={dayjs(block.start_time, TIME_FORMAT)}
                            onChange={(value) => {
                              if (!value) return;
                              const start_time = value.format(TIME_FORMAT);
                              updateBlocks(
                                row.day,
                                row.blocks.map((b, i) =>
                                  i === index
                                    ? {
                                        ...b,
                                        start_time,
                                        // Bump end forward with start rather
                                        // than leaving it blank if it would no
                                        // longer be after it — the two
                                        // pickers are independent (unlike a
                                        // RangePicker), so this can't rely on
                                        // the picker itself to keep the block
                                        // valid.
                                        end_time:
                                          b.end_time <= start_time
                                            ? value
                                                .add(TIME_MINUTE_STEP, "minute")
                                                .format(TIME_FORMAT)
                                            : b.end_time,
                                      }
                                    : b,
                                ),
                              );
                            }}
                          />
                          <Typography.Text type="secondary">→</Typography.Text>
                          <AutoCloseTimePicker
                            format={TIME_FORMAT}
                            minuteStep={TIME_MINUTE_STEP}
                            allowClear={false}
                            disabledTime={disabledEndTime(block.start_time)}
                            value={dayjs(block.end_time, TIME_FORMAT)}
                            onChange={(value) => {
                              if (!value) return;
                              updateBlocks(
                                row.day,
                                row.blocks.map((b, i) =>
                                  i === index
                                    ? { ...b, end_time: value.format(TIME_FORMAT) }
                                    : b,
                                ),
                              );
                            }}
                          />
                          <Button
                            type="text"
                            size="small"
                            icon={<CloseCircleOutlined />}
                            aria-label={translate(
                              "class_settings.employee_management.remove_time_block",
                            )}
                            onClick={() =>
                              updateBlocks(
                                row.day,
                                row.blocks.filter((_, i) => i !== index),
                              )
                            }
                          />
                        </Flex>
                      ))}
                      <Button
                        type="link"
                        size="small"
                        style={{ paddingLeft: 0, width: "fit-content" }}
                        onClick={() =>
                          updateBlocks(row.day, [
                            ...row.blocks,
                            nextBlockDefault(row.blocks),
                          ])
                        }
                      >
                        {translate("class_settings.employee_management.add_time_block")}
                      </Button>
                    </>
                  )}
                </Flex>
              )}
            </Flex>
          ))}
        </Flex>
      </Flex>
      </Card>
      </ReadOnlyWhenRestricted>
      {workingHoursPreviewWarning}
    </>
  );
};

export default InstructorWorkingHours;
